import React from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@pagespeedify/ui';
import { Trash2, CheckCircle, AlertCircle, Clock, Minus, Play, Eye, EyeOff } from 'lucide-react';
import { useNotificationDeleteQueue, QueuedDeleteNotification } from './NotificationDeleteQueueContext';
import { useApi } from '../../lib/apiClient';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

const NotificationDeleteQueuePanel: React.FC = () => {
  const {
    queuedDeleteNotifications,
    removeNotificationFromDeleteQueue,
    clearDeleteQueue,
    clearCompletedDeletes,
    updateDeleteNotificationStatus,
    isDeleteQueueVisible,
    setDeleteQueueVisible,
    isProcessingDelete,
    setIsProcessingDelete
  } = useNotificationDeleteQueue();
  const apiClient = useApi();
  const queryClient = useQueryClient();

  if (!isDeleteQueueVisible && queuedDeleteNotifications.length === 0) {
    return null;
  }

  const getStatusIcon = (status: QueuedDeleteNotification['status']) => {
    switch (status) {
      case 'queued':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'deleting':
        return <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />;
      case 'deleted':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: QueuedDeleteNotification['status']) => {
    switch (status) {
      case 'queued': return 'Queued';
      case 'deleting': return 'Deleting...';
      case 'deleted': return 'Deleted';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  };

  const handleProcessDeleteQueue = async () => {
    if (isProcessingDelete) return;
    const queuedItems = queuedDeleteNotifications.filter(n => n.status === 'queued');
    if (queuedItems.length === 0) {
      toast.info('No notifications in delete queue to process');
      return;
    }
    setIsProcessingDelete(true);
    
    // Mark all as deleting for UI
    queuedItems.forEach(notification => updateDeleteNotificationStatus(notification.id, 'deleting'));
    
    try {
      // Delete notifications one by one (or implement batch delete if API supports it)
      for (const notification of queuedItems) {
        try {
          await apiClient.delete(`/notifications/${notification.id}`);
          updateDeleteNotificationStatus(notification.id, 'deleted');
        } catch (err: any) {
          updateDeleteNotificationStatus(notification.id, 'failed', err?.message || 'Failed to delete');
        }
      }
      
      const successCount = queuedItems.filter(n => 
        queuedDeleteNotifications.find(q => q.id === n.id)?.status === 'deleted'
      ).length;
      
      if (successCount > 0) {
        toast.success(`${successCount} notification${successCount !== 1 ? 's' : ''} deleted`);
      }
      
      // Invalidate and refetch notifications query
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.refetchQueries({ queryKey: ['notifications'], exact: true });
      await queryClient.invalidateQueries({ queryKey: ['unreadNotificationsCount'] });
      await queryClient.refetchQueries({ queryKey: ['unreadNotificationsCount'], exact: true });
      
      // Clear completed deletes after a short delay
      setTimeout(() => {
        clearCompletedDeletes();
      }, 2000);
    } catch (err) {
      toast.error('Unable to delete notifications. Please try again.');
    }
    
    setIsProcessingDelete(false);
  };

  const queuedCount = queuedDeleteNotifications.filter(n => n.status === 'queued').length;
  const deletedCount = queuedDeleteNotifications.filter(n => n.status === 'deleted').length;
  const failedCount = queuedDeleteNotifications.filter(n => n.status === 'failed').length;

  return (
    <div className="fixed right-4 bottom-4 z-50 w-96 max-h-[70vh] overflow-hidden">
      <Card className="shadow-lg border border-border bg-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Queue ({queuedDeleteNotifications.length})
            </CardTitle>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteQueueVisible(!isDeleteQueueVisible)}
                className="h-8 w-8 p-0"
              >
                {isDeleteQueueVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearDeleteQueue}
                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                disabled={isProcessingDelete}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        {isDeleteQueueVisible && (
          <CardContent className="pt-0">
            {queuedDeleteNotifications.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No notifications in delete queue.
              </p>
            ) : (
              <>
                <div className="max-h-60 overflow-y-auto space-y-2 mb-4">
                  {queuedDeleteNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="flex items-start gap-2 p-2 bg-muted rounded text-sm"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getStatusIcon(notification.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{notification.subject}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{getStatusText(notification.status)}</span>
                          {notification.error && <span className="text-red-500 ml-2">{notification.error}</span>}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeNotificationFromDeleteQueue(notification.id)}
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500"
                        disabled={isProcessingDelete && notification.status === 'deleting'}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                {queuedCount > 0 && (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleProcessDeleteQueue}
                      disabled={isProcessingDelete}
                      className="flex-1"
                      size="sm"
                    >
                      {isProcessingDelete ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Delete {queuedCount} Notification{queuedCount !== 1 ? 's' : ''}
                        </>
                      )}
                    </Button>
                    {(deletedCount > 0 || failedCount > 0) && (
                      <Button
                        onClick={clearCompletedDeletes}
                        disabled={isProcessingDelete}
                        variant="outline"
                        size="sm"
                        className="px-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default NotificationDeleteQueuePanel;
